/**
 * AuthorBio — YMYL trust signal for cannabis editorial pages.
 *
 * Google (and Perplexity / ChatGPT / Claude) actively down-rank cannabis
 * content that lacks credentialed authorship. Attaching a real editorial
 * team byline + Person/Organization schema is a documented ranking factor
 * for the "Your Money or Your Life" content family.
 *
 * Rendered inline at the bottom of long-form pages (brand editorials,
 * strain guides, FAQ answers). Also emits a Person JSON-LD entity that
 * the parent page's Article schema can `author`-reference.
 */
import { ShieldCheck } from "lucide-react";

const TEAM = {
    name: "ZenZest Editorial Team",
    jobTitle: "Licensed NY OCM cannabis retailers",
    url: "https://weeddispensarynearme.com/",
    image: "https://weeddispensarynearme.com/zenzest-badge.png",
    description:
        "Reviewed and maintained by ZenZest budtenders working the floor at our Queens and Staten Island dispensaries. Every editorial is fact-checked against current NY Office of Cannabis Management (OCM) regulations, live product lab reports (COAs), and hands-on customer feedback captured during in-store consultations.",
    knowsAbout: [
        "New York adult-use cannabis retail",
        "NY OCM regulation and compliance",
        "Cannabis product COAs and lab reports",
        "Strain effects and terpene profiles",
        "Cannabis first-time-user guidance",
    ],
    memberOf: {
        "@type": "Organization",
        name: "ZenZest",
        url: "https://weeddispensarynearme.com/",
    },
};

export function authorBioSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: TEAM.name,
        jobTitle: TEAM.jobTitle,
        url: TEAM.url,
        image: TEAM.image,
        description: TEAM.description,
        knowsAbout: TEAM.knowsAbout,
    };
}

export default function AuthorBio({ reviewedDate }) {
    return (
        <aside
            data-testid="author-bio"
            className="mt-12 pt-8 border-t border-zen-line bg-zen-bg/40 -mx-6 sm:-mx-10 px-6 sm:px-10 py-8"
        >
            <div className="max-w-[900px] mx-auto flex items-start gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-full overflow-hidden border border-zen-line bg-zen-elevated flex items-center justify-center">
                    <img
                        src={TEAM.image}
                        alt="ZenZest Editorial Team"
                        loading="lazy"
                        className="w-10 h-10 object-contain"
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive mb-1 inline-flex items-center gap-2">
                        <ShieldCheck size={12} /> Reviewed by
                    </p>
                    <h3 className="font-serif text-lg text-zen-ink leading-tight">
                        {TEAM.name}
                    </h3>
                    <p className="text-[12px] text-zen-ink-soft mt-1">
                        {TEAM.jobTitle}
                    </p>
                    <p className="text-[13px] text-zen-ink-soft mt-3 leading-relaxed">
                        {TEAM.description}
                    </p>
                    {reviewedDate && (
                        <p className="text-[11px] text-zen-ink-soft mt-3">
                            <strong>Last reviewed:</strong>{" "}
                            <time dateTime={reviewedDate}>
                                {new Date(reviewedDate).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </time>
                        </p>
                    )}
                </div>
            </div>
        </aside>
    );
}
